import EventEmitter from 'events'
import vueCookie from 'vue-cookie'
import protooClient from 'protoo-client'
import * as mediasoupClient from 'mediasoup-client'
import deviceInfo from '@/lib/deviceInfo'
import urlFactory from '@/lib/mediasoup/urlFactory'
import {
  VIDEO_CONSTRAINS,
  PC_PROPRIETARY_CONSTRAINTS,
  WEBCAM_SIMULCAST_ENCODINGS,
  WEBCAM_KSVC_ENCODINGS
} from '@/lib/mediasoup/constants'
import store from '@/store'

export default class WebRTCClient extends EventEmitter {
  constructor({
    userId,
    organizationId,
    videoRoomId,
    videoRoomMemberId,
    displayName,
    roomPassword,
    mediaVideoInputId,
    mediaAudioInputId,
    guestToken
  } = {}) {
    super()

    this._entered = false
    this._device = deviceInfo()
    this._peerId = userId
    this._displayName = displayName
    this._roomPassword = roomPassword
    this._mediaVideoInputId = mediaVideoInputId
    this._mediaAudioInputId = mediaAudioInputId
    this._guestToken = guestToken
    this._consumers = new Map()
    this._dataConsumers = new Map()
    this._webcam = {
      device: null,
      resolution: 'hd'
    }

    const transport = new protooClient.WebSocketTransport(urlFactory(videoRoomId, this._peerId))

    let authentication

    if (this._guestToken) {
      authentication = {
        token: this._guestToken,
        client: null,
        uid: null
      }
    } else {
      let session
      try {
        session = JSON.parse(vueCookie.get('session')) || []
      } catch (e) {
        session = []
      }
      authentication = {
        token: 'access-token' in session ? session['access-token'] : null,
        client: 'client' in session ? session['client'] : null,
        uid: 'uid' in session ? session['uid'] : null
      }
    }
    const enterRoomParams = {
      organizationId: organizationId,
      videoRoomId: videoRoomId,
      videoRoomMemberId: videoRoomMemberId,
      displayName: displayName,
      roomPassword: roomPassword,
      ...authentication
    }
    this._initProtooClient(transport, enterRoomParams)
  }

  get _peers() {
    return store.getters['roomPeers/peers']
  }

  get _frontPeerIds() {
    return store.getters['roomPeers/frontPeerIds']
  }

  _initProtooClient(transport, enterRoomParams) {
    this._protoo = new protooClient.Peer(transport)

    this._protoo.on('open', () => this._enterRoom(enterRoomParams))

    this._protoo.on('failed', () => this.emit('closeProtoo'))

    this._protoo.on('disconnected', () => {
      this._sendTransport?.close()
      this._recvTransport?.close()
      this._sendTransport = null
      this._recvTransport = null
    })

    this._protoo.on('close', () => this.emit('closeProtoo'))

    this._protoo.on('request', async (request, accept) => {
      switch (request.method) {
        case 'newConsumer': {
          const {
            peerId,
            producerId,
            id,
            kind,
            rtpParameters,
            appData,
          } = request.data

          let codecOptions
          if (kind === 'audio') codecOptions = { opusStereo : 1 }

          try {
            const consumer = await this._recvTransport.consume({
              id,
              producerId,
              kind,
              rtpParameters,
              codecOptions,
              appData: { ...appData, peerId }
            })

            this._consumers.set(consumer.id, consumer)

            consumer.on('transportclose', () => {
              this._consumers.delete(consumer.id)
            })

            const stream = new MediaStream
            const peer = this._peers[peerId]

            if (peer) {
              if (appData.share) {
                stream.addTrack(consumer.track)
                store.dispatch('roomWhiteboard/setScreenShareStream', stream)
                store.dispatch('roomWhiteboard/changeScreenShare', 2)
                store.dispatch('roomPeers/setScreenSharerPeerId', peerId)
              } else if (appData.shareAudio) {
                stream.addTrack(consumer.track)
                store.dispatch('roomWhiteboard/setScreenShareAudioStream', stream)
              } else {
                switch (consumer.track.kind) {
                  case 'video': {
                    stream.addTrack(consumer.track)
                    peer.videoStream = stream
                    peer.webcamState = true

                    if (!this._frontPeerIds.includes(peerId)) {
                      this._pauseConsumer(consumer)
                    }
                    break
                  }
                  case 'audio': {
                    stream.addTrack(consumer.track)
                    peer.audioStream = stream
                    peer.micState = true
                    break
                  }
                }
                store.dispatch('roomPeers/setPeer', { [peerId]: peer })
              }
            }
            accept()
          } catch (e) {
            console.error(`newConsumer request | failed:${e}`)
          }
          break
        }
        case 'newDataConsumer': {
          const {
            peerId,
            dataProducerId,
            id,
            sctpStreamParameters,
            label,
            protocol,
            appData
          } = request.data

          try {
            const dataConsumer = await this._recvTransport.consumeData({
              id,
              dataProducerId,
              sctpStreamParameters,
              label,
              protocol,
              appData: { ...appData, peerId }
            })

            this._dataConsumers.set(dataConsumer.id, dataConsumer)

            dataConsumer.on('transportclose', () => {
              this._dataConsumers.delete(dataConsumer.id)
            })

            dataConsumer.on('close', () => {
              this._dataConsumers.delete(dataConsumer.id)
            })

            dataConsumer.on('message', (json) => {
              const message = JSON.parse(json)
              switch (dataConsumer.label) {
                case 'chat': {
                  const peer = this._peers[peerId]
                  store.dispatch('roomChatMessages/setMessage', {
                    displayName: peer.displayName,
                    content: message.content
                  })
                  this.emit('receivedChatMessage')
                  break
                }
                case 'roomControl': {
                  switch (message.type) {
                    case 'raisedHands': {
                      store.dispatch('roomPeers/setRaisedHandsStateOfPeer', {
                        peerId: peerId,
                        raisedHandsState: message.state
                      })
                      this.emit('raisedHands')
                      break
                    }
                  }
                  break
                }
                case 'whiteboard': {
                  store.dispatch('roomWhiteboard/remoteAction', message)
                  break
                }
              }
            })

            accept()
          } catch (e) {
            console.error(`recvTransport consumeData | failed:${e}`)
          }
          break
        }
      }
    })

    this._protoo.on('notification', (notification) => {
      let peer

      switch (notification.method) {
        case 'newPeer': {
          peer = notification.data
          store.dispatch('roomPeers/setPeer', {
            [peer.id]: {
              displayName: peer.displayName,
              sortedAt: new Date()
            }
          })
          break
        }
        case 'peerClosed': {
          store.dispatch('roomPeers/removePeer', notification.data.peerId)
          break
        }
        case 'consumerClosed': {
          const { consumerId } = notification.data
          const consumer = this._consumers.get(consumerId)

          if (!consumer) break

          if (consumer.appData.share) {
            store.dispatch('roomWhiteboard/setScreenShareStream', null)
            store.dispatch('roomWhiteboard/changeScreenShare', 0)
            store.dispatch('roomPeers/setScreenSharerPeerId', null)
          } else if (consumer.appData.shareAudio) {
            store.dispatch('roomWhiteboard/setScreenShareAudioStream', null)
          } else {
            peer = this._peers[consumer.appData.peerId]
            switch (consumer.track.kind) {
              case 'video': {
                peer.videoStream = null
                peer.webcamState = false
                break
              }
              case 'audio': {
                peer.audioStream = null
                peer.micState = false
                break
              }
            }
            store.dispatch('roomPeers/setPeer', { [consumer.appData.peerId]: peer })
          }
          consumer.close()
          this._consumers.delete(consumerId)
          break
        }
        case 'activeSpeaker': {
          const { peerId } = notification.data
          if (store.getters['roomPeers/activeSpeakerPeerId'] != peerId) {
            store.dispatch('roomPeers/setActiveSpeakerPeerId', peerId)
          }
          break
        }
        case 'roomStatus': {
          const { remainTime } = notification.data
          if (remainTime <= 0) {
            this.emit('endMeeting')
          } else if (remainTime == 1 || remainTime == 5 || remainTime == 10) {
            this.emit('displayToastRemainTime', remainTime)
          }
          // TODO: 5分前通知
          // } else if (1 <= remainTime && remainTime <= 5) {
          // }
          break
        }
      }
    })
  }

  _initSendTransport(produceTransportInfo) {
    this._sendTransport = this._mediasoupDevice.createSendTransport({
      id: produceTransportInfo.id,
      iceParameters: produceTransportInfo.iceParameters,
      iceCandidates: produceTransportInfo.iceCandidates,
      iceServers: produceTransportInfo.iceServers,
      dtlsParameters: produceTransportInfo.dtlsParameters,
      sctpParameters: produceTransportInfo.sctpParameters,
      proprietaryConstraints: PC_PROPRIETARY_CONSTRAINTS
    })

    this._sendTransport.on('connect', ({ dtlsParameters }, callback, errback) => {
      this._protoo.request('connectWebRtcTransport', {
        transportId : this._sendTransport.id,
        dtlsParameters
      })
      .then(callback)
      .catch(errback)
    })

    this._sendTransport.on('produce', async ({ kind, rtpParameters, appData }, callback, errback) => {
      try {
        const { id } = await this._protoo.request('produce', {
          transportId : this._sendTransport.id,
          kind,
          rtpParameters,
          appData
        })
        callback({ id })
      } catch (e) {
        errback(e)
      }
    })

    this._sendTransport.on('producedata', async ({
      sctpStreamParameters,
      label,
      protocol,
      appData
    }, callback, errback) => {
      try {
        const { id } = await this._protoo.request('produceData', {
          transportId: this._sendTransport.id,
          sctpStreamParameters,
          label,
          protocol,
          appData
        })
        callback({ id })
      } catch (e) {
        errback(e)
      }
    })
  }

  _initRecvTransport(consumeTransportInfo) {
    this._recvTransport = this._mediasoupDevice.createRecvTransport({
      id: consumeTransportInfo.id,
      iceParameters: consumeTransportInfo.iceParameters,
      iceCandidates: consumeTransportInfo.iceCandidates,
      iceServers: consumeTransportInfo.iceServers,
      dtlsParameters: consumeTransportInfo.dtlsParameters,
      sctpParameters: consumeTransportInfo.sctpParameters
    })

    this._recvTransport.on('connect', ({ dtlsParameters }, callback, errback) => {
      this._protoo.request('connectWebRtcTransport', {
        transportId: this._recvTransport.id,
        dtlsParameters
      })
      .then(callback)
      .catch(errback)
    })
  }

  _enterRoom(params) {
    if (this._entered) return
    this._protoo.request('enterRoom', params).then(() => {
      this.emit('enterRoomResult', {
        entered: true
      })
      this._entered = true
    }).catch((data) => {
      const { statusCode } = JSON.parse(data.message)
      this.emit('enterRoomResult', {
        entered: false,
        errorCode: statusCode
      })
    })
  }

  // NOTE: 入室時に入力デバイスを決定する仕様に変更したため廃止
  // async _updateWebcams() {
  //   const webcams = new Map()
  //   const devices = await navigator.mediaDevices.enumerateDevices()
  //
  //   for (const device of devices) {
  //     if (device.kind !== 'videoinput') continue
  //     webcams.set(device.deviceId, device)
  //   }
  //
  //   const array = Array.from(webcams.values())
  //   const len = array.length
  //   const currentWebcamId = this._webcam.device ? this._webcam.device.deviceId : undefined
  //
  //   if (len === 0) {
  //     this._webcam.device = null
  //   } else if (!webcams.has(currentWebcamId)) {
  //     this._webcam.device = array[0]
  //   }
  // }

  async _pauseConsumer(consumer) {
    if (!consumer || consumer.paused) return
    try {
      await this._protoo.request('pauseConsumer', { consumerId: consumer.id })
      consumer.pause()
    } catch (e) {
      console.error(`_pauseConsumer | failed:${e}`)
    }
  }

  async _resumeConsumer(consumer) {
    if (!consumer || !consumer.paused) return
    try {
      await this._protoo.request('resumeConsumer', { consumerId: consumer.id })
      consumer.resume()
    } catch (e) {
      console.error(`_resumeConsumer | failed:${e}`)
    }
  }

  async joinRoom() {
    try {
      this._mediasoupDevice = new mediasoupClient.Device({})
      const routerRtpCapabilities = await this._protoo.request('getRouterRtpCapabilities')

      // NOTE: safari <=> Firefox対応
      if (this._device.flag == 'safari') {
        routerRtpCapabilities.headerExtensions =
          routerRtpCapabilities.headerExtensions.filter(
            (ext) => ext.uri !== 'urn:3gpp:video-orientation'
          )
      }
      await this._mediasoupDevice.load({ routerRtpCapabilities })

      const produceTransportInfo = await this._protoo.request('createWebRtcTransport', {
        producing: true,
        consuming: false,
        sctpCapabilities: this._mediasoupDevice.sctpCapabilities
      })
      this._initSendTransport(produceTransportInfo)

      const consumeTransportInfo = await this._protoo.request('createWebRtcTransport', {
        producing: false,
        consuming: true,
        sctpCapabilities: this._mediasoupDevice.sctpCapabilities
      })
      this._initRecvTransport(consumeTransportInfo)

      const { peers } = await this._protoo.request('join', {
        displayName: this._displayName,
        device: this._device,
        rtpCapabilities: this._mediasoupDevice.rtpCapabilities,
        sctpCapabilities: this._mediasoupDevice.sctpCapabilities,
        roomPassword: this._roomPassword
      })
      if (peers.length) {
        store.dispatch('roomPeers/setPeer', peers.reduce((result, peer) => {
          result[peer.id] = {
            displayName: peer.displayName,
            aboriginal: true,
            sortedAt: new Date()
          }
          return result
        }, {}))
      }
      store.dispatch('roomPeers/setMyPeer', {
        id: this._peerId,
        displayName: this._displayName
      })
      this.emit('joinRoomResult', true)
    } catch (e) {
      console.error(`joinRoom() | failed:${e}`)
      this.emit('joinRoomResult', false)
    }
  }

  async enableMic() {
    if (this._micProducer) return

    let track

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: this._mediaAudioInputId
        }
      })
      track = stream.getAudioTracks()[0]

      this._micProducer = await this._sendTransport.produce({
        track,
        codecOptions: {
          opusStereo: 1,
          opusDtx: 1
        }
      })

      store.dispatch('roomPeers/setMyPeer', {
        micState: true
      })

      this._micProducer.on('transportclose', () => {
        this._micProducer = null
      })

      this._micProducer.on('trackended', () => {
        this.disableMic().catch(() => {})
      })
    } catch (e) {
      console.error(`enableMic() | failed:${e}`)
      if (track) {
        track.stop()
      }
    }
  }

  async enableWebcam() {
    if (this._webcamProducer) return

    let track

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: this._mediaVideoInputId,
          frameRate: { ideal: 10, max: 30 },
          ...VIDEO_CONSTRAINS['hd']
        }
      })
      track = stream.getVideoTracks()[0]

      const firstVideoCodec = this._mediasoupDevice.rtpCapabilities.codecs.find((c) => c.kind === 'video')
      let encodings

      if (firstVideoCodec.mimeType.toLowerCase() === 'video/vp9') {
        encodings = WEBCAM_KSVC_ENCODINGS
      } else {
        encodings = WEBCAM_SIMULCAST_ENCODINGS
      }

      this._webcamProducer = await this._sendTransport.produce({
        track,
        encodings,
        codecOptions: {
          videoGoogleStartBitrate: 1000
        }
      })

      store.dispatch('roomPeers/setMyPeer', {
        videoStream: stream,
        webcamState: true
      })

      this._webcamProducer.on('transportclose', () => {
        this._webcamProducer = null
      })

      this._webcamProducer.on('trackended', () => {
        this.disableWebcam().catch(() => {})
      })
    } catch (e) {
      console.error(`enableWebcam() | failed:${e}`)
      if (track) {
        track.stop()
      }
    }
  }

  async enableScreenShare() {
    if (this._screenShareProducer) return
    let track, audioTrack

    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        audio : true,
        video : {
          displaySurface: 'monitor',
          logicalSurface: true,
          cursor: true,
          width: { max: 1440 },
          height: { max: 810 },
          frameRate: { max: 5 }
        }
      })
      track = stream.getVideoTracks()[0]

      this._screenShareProducer = await this._sendTransport.produce({
        track,
        codecOptions: {
          videoGoogleStartBitrate : 1000
        },
        appData: {
          share: true
        }
      })

      store.dispatch('roomWhiteboard/setScreenShareStream', stream)
      store.dispatch('roomWhiteboard/changeScreenShare', 1)
      store.dispatch('roomPeers/setScreenSharerPeerId', this._peerId)

      this._screenShareProducer.on('transportclose', () => {
        this._screenShareProducer = null
      })

      this._screenShareProducer.on('trackended', () => {
        this.disableScreenShare().catch(() => {})
      })

      audioTrack = stream.getAudioTracks()[0]
      if (audioTrack) {
        this._screenShareAudioProducer = await this._sendTransport.produce({
          track: audioTrack,
          codecOptions: {
            opusStereo: 1,
            opusDtx: 1
          },
          appData: {
            shareAudio: true
          }
        })

        this._screenShareAudioProducer.on('transportclose', () => {
          this._screenShareAudioProducer = null
        })
      }
    } catch (e) {
      console.error(`enableShare() | failed:${e}`)
      if (track) track.stop()
      if (audioTrack) audioTrack.stop()
    }
  }

  async enableRoomControlDataProducer() {
    try {
      this._roomControlDataProducer = await this._sendTransport.produceData({
        ordered: true,
        maxRetransmits: 1,
        label: 'roomControl',
        priority: 'high',
        appData: { info: 'roomControl-DataProducer' }
      })

      this._roomControlDataProducer.on('transportclose', () => {
        this._roomControlDataProducer = null
      })

      this._roomControlDataProducer.on('close', () => {
        this._roomControlDataProducer = null
      })
    } catch (e) {
      console.error(`enableRoomControlDataProducer() | failed:${e}`)
    }
  }

  async enableChatDataProducer() {
    try {
      this._chatDataProducer = await this._sendTransport.produceData({
        ordered: true,
        maxRetransmits: 1,
        label: 'chat',
        priority: 'medium',
        appData: { info: 'chat-DataProducer' }
      })

      this._chatDataProducer.on('transportclose', () => {
        this._chatDataProducer = null
      })

      this._chatDataProducer.on('close', () => {
        this._chatDataProducer = null
      })
    } catch (e) {
      console.error(`enableChatDataProducer() | failed:${e}`)
    }
  }

  async enableWhiteboardDataProducer() {
    try {
      this._whiteboardDataProducer = await this._sendTransport.produceData({
        ordered: false,
        maxRetransmits: 1,
        label: 'whiteboard',
        priority: 'medium',
        appData: { info: 'whiteboard-DataProducer' }
      })

      this._whiteboardDataProducer.on('transportclose', () => {
        this._whiteboardDataProducer = null
      })

      this._whiteboardDataProducer.on('close', () => {
        this._whiteboardDataProducer = null
      })
    } catch (e) {
      console.error(`enableWhiteboardDataProducer() | failed:${e}`)
    }
  }

  async disableMic() {
    this._micProducer.close()

    store.dispatch('roomPeers/setMyPeer', {
      micState: false
    })

    try {
      await this._protoo.request('closeProducer', { producerId: this._micProducer.id })
    } catch (e) {
      console.error(`disableMic() | failed:${e}`)
    }
    this._micProducer = null
  }

  async disableWebcam() {
    this._webcamProducer.close()

    store.dispatch('roomPeers/setMyPeer', {
      videoStream: null,
      webcamState: false
    })

    try {
      await this._protoo.request('closeProducer', { producerId: this._webcamProducer.id })
    } catch (e) {
      console.error(`disableWebcam() | failed:${e}`)
    }
    this._webcamProducer = null
  }

  async disableScreenShare() {
    if (!this._screenShareProducer) return

    this._screenShareProducer.close()

    if (this._screenShareAudioProducer) {
      this._screenShareAudioProducer.close()
    }

    store.dispatch('roomWhiteboard/setScreenShareStream', null)
    store.dispatch('roomWhiteboard/setScreenShareAudioStream', null)
    store.dispatch('roomWhiteboard/changeScreenShare', 0)
    store.dispatch('roomPeers/setScreenSharerPeerId', null)

    try {
      await this._protoo.request('closeProducer', {
        producerId: this._screenShareProducer.id
      })
    } catch (e) {
      console.error(`_protoo request closeProducer() | failed:${e}`)
    }
    this._screenShareProducer = null
    this._screenShareAudioProducer = null
  }

  controlConsumers() {
    for(const consumer of this._consumers.values()) {
      if (
        consumer.kind === 'audio'
        || consumer.appData.share
        || consumer.appData.shareAudio
        || !this._peers[consumer.appData.peerId]
      ) continue
      /**
       * NOTE: pause/resumeの条件
       * perPageが0の場合は強制的に全peerをresume
       * perPageが1以上かつ画面共有者 or フロントに存在するpeerはresume
       * それ以外は全てpause
       */
      if (
        0 != store.getters['roomPeers/carousel'].perPage &&
        (
          consumer.appData.peerId == store.getters['roomPeers/screenSharerPeerId'] ||
          this._frontPeerIds.includes(consumer.appData.peerId)
        )
      ) {
        this._resumeConsumer(consumer)
      } else {
        this._pauseConsumer(consumer)
      }
    }
  }

  sendRaisedHands(raised) {
    if (!this._roomControlDataProducer) return
    try {
      store.dispatch('roomPeers/setMyPeer', {
        raisedHandsState: raised
      })
      this._roomControlDataProducer.send(JSON.stringify({
        type: 'raisedHands',
        state: raised
      }))
    } catch (e) {
      console.error(`sendRaisedHands() | failed:${e}`)
    }
  }

  sendChatMessage(message) {
    if (!this._chatDataProducer) return
    try {
      store.dispatch('roomChatMessages/setMessage', {
        displayName: this._displayName,
        content: message
      })
      this._chatDataProducer.send(JSON.stringify({
        content: message
      }))
    } catch (e) {
      console.error(`sendChatMessage() | failed:${e}`)
    }
  }

  sendWhiteboardMessage(message) {
    if (!this._whiteboardDataProducer) return
    try {
      this._whiteboardDataProducer.send(message)
    } catch (e) {
      console.error(`sendWhiteboardMessage() | failed:${e}`)
    }
  }

  async updateMic(mediaAudioInputId) {
    await this.disableMic()
    this._mediaAudioInputId = mediaAudioInputId
    await this.enableMic()
  }

  async updateWebcam(mediaVideoInputId) {
    await this.disableWebcam()
    this._mediaVideoInputId = mediaVideoInputId
    await this.enableWebcam()
  }

  close() {
    this._protoo?.close()
    this._sendTransport?.close()
    this._recvTransport?.close()
  }
}
